import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CurrencyPaymentGatewayDto, PaymentGatewayConfigDto, CreateCurrencyPaymentGatewayRequestDto, UpdateCurrencyPaymentGatewayRequestDto } from '../../_core/_model/payment-admin';
import { NotificationService } from '../../_core/_services/notification.service';
import { WithdrawalAdminService } from '../../_core/_services/withdrawal-admin.service';
import { MainCurrencyDto } from '../../_core/_model/currency';
import { ApiService } from '../../_core/_services/api.service';
import { catchError, forkJoin, of } from 'rxjs';

@Component({
  selector: 'app-withdrawal-gateway-mappings',
  standalone: false,
  templateUrl: './withdrawal-gateway-mappings.html',
  styleUrl: './withdrawal-gateway-mappings.css',
})

export class WithdrawalGatewayMappings implements OnInit {
  loading = false;
  saving = false;

  currencies: MainCurrencyDto[] = [];
  currencyCountryMap: Record<string, string[]> = {};
  countryOptions: string[] = [];
  mappings: CurrencyPaymentGatewayDto[] = [];
  filteredMappings: CurrencyPaymentGatewayDto[] = [];
  configs: PaymentGatewayConfigDto[] = [];

  filterCurrencyCode = '';
  filterWorkflow = 'Withdrawal';

  showModal = false;
  editingId: string | null = null;

  form!: FormGroup;

  readonly workflowOptions = ['Deposit', 'Withdrawal', 'Both'];
  readonly paymentMethodOptions = ['bank-transfer', 'mobile-money', 'wallet', 'interac'];
  readonly frontendHintOptions = ['redirect', 'inline-sdk', 'hosted-form', 'bank-form'];
  readonly customerTypeOptions = ['Individual', 'Business'];

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private withdrawalAdminService: WithdrawalAdminService,
    private notifyService: NotificationService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.form.get('currencyCode')?.valueChanges.subscribe((currencyCode) => {
      this.applyCountryCodeForCurrency(currencyCode);
    });
    this.loadInitialData();
    this.loadMain();
  }

  buildForm(): void {
    this.form = this.fb.group({
      currencyCode: ['', [Validators.required]],
      gatewayConfigId: ['', [Validators.required]],
      isDefault: [false],
      isActive: [true],
      priority: [1, [Validators.required, Validators.min(1)]],
      minAmount: [null],
      maxAmount: [null],
      customerType: [''],
      countryCode: [''],
      workflow: ['Withdrawal', [Validators.required]],
      paymentMethod: ['bank-transfer'],
      frontendHint: ['bank-form'],
      metadataJson: ['']
    });
  }

  loadMain() {
    this.api.getMain().subscribe({
      next: (res) => {
        this.currencies = (res.data?.currencies ?? []).filter(c => c.status === 'Active');
        const countries = (res.data?.countries ?? []).filter(c => c.status === 'Active');
        this.loadCurrencyCountryMap(countries.map(country => country.code));
      },
      complete: () => (this.loading = false),
    });
  }

  loadCurrencyCountryMap(countryCodes: string[]): void {
    if (!countryCodes.length) {
      this.currencyCountryMap = {};
      return;
    }

    forkJoin(
      countryCodes.map(countryCode =>
        this.api.getCurrencyCountry(countryCode, 1, 1000).pipe(
          catchError(() => of(null))
        )
      )
    ).subscribe((responses) => {
      const nextMap: Record<string, string[]> = {};

      responses.forEach((res) => {
        (res?.data || []).forEach((item) => {
          const currencyCode = item.currency?.code?.toUpperCase();
          const countryCode = item.country?.code?.toUpperCase();

          if (!currencyCode || !countryCode) {
            return;
          }

          if (!nextMap[currencyCode]) {
            nextMap[currencyCode] = [];
          }

          if (!nextMap[currencyCode].includes(countryCode)) {
            nextMap[currencyCode].push(countryCode);
          }
        });
      });

      this.currencyCountryMap = nextMap;
      this.applyCountryCodeForCurrency(this.form.get('currencyCode')?.value);
    });
  }

  applyCountryCodeForCurrency(currencyCode: string | null | undefined): void {
    const code = String(currencyCode || '').trim().toUpperCase();
    const options = code ? this.currencyCountryMap[code] || [] : [];
    const currentCountryCode = String(this.form?.get('countryCode')?.value || '').trim().toUpperCase();
    const nextCountryCode = options.includes(currentCountryCode)
      ? currentCountryCode
      : options[0] || '';

    this.countryOptions = options;

    if (this.form?.get('countryCode')?.value !== nextCountryCode) {
      this.form.get('countryCode')?.setValue(nextCountryCode, { emitEvent: false });
    }
  }

  loadInitialData(): void {
    this.loading = true;

    this.withdrawalAdminService.getConfigs().subscribe({
      next: (res) => {
        this.configs = res.data || [];
      },
      error: () => {
        this.notifyService.showError('Failed to load gateway configs', 'Error');
      }
    });

    this.loadMappings();
  }

  loadMappings(): void {
    this.loading = true;

    this.withdrawalAdminService
      .getCurrencyMappings(this.filterCurrencyCode || undefined, this.filterWorkflow || undefined)
      .subscribe({
        next: (res) => {
          this.mappings = res.data || [];
          this.applyClientFilter();
        },
        error: (err) => {
          this.notifyService.showError(
            err?.error?.Error?.Details || err?.error?.message || 'Failed to load currency mappings',
            'Error'
          );
          this.mappings = [];
          this.filteredMappings = [];
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  applyClientFilter(): void {
    this.filteredMappings = this.mappings.filter((x) => {
      const currencyOk = !this.filterCurrencyCode ||
        x.currencyCode.toLowerCase().includes(this.filterCurrencyCode.toLowerCase());

      const workflowOk = !this.filterWorkflow || x.workflow === this.filterWorkflow;

      return currencyOk && workflowOk;
    });
  }

  openCreateModal(): void {
    this.editingId = null;
    this.form.reset({
      currencyCode: '',
      gatewayConfigId: '',
      isDefault: false,
      isActive: true,
      priority: 1,
      minAmount: null,
      maxAmount: null,
      customerType: '',
      countryCode: '',
      workflow: 'Withdrawal',
      paymentMethod: 'bank-transfer',
      frontendHint: 'bank-form',
      metadataJson: ''
    });

    this.showModal = true;
  }

  openEditModal(item: CurrencyPaymentGatewayDto): void {
    this.editingId = item.id;

    this.form.patchValue({
      currencyCode: item.currencyCode,
      gatewayConfigId: item.gatewayConfigId,
      isDefault: item.isDefault,
      isActive: item.isActive,
      priority: item.priority,
      minAmount: item.minAmount,
      maxAmount: item.maxAmount,
      customerType: item.customerType || '',
      countryCode: item.countryCode || '',
      workflow: item.workflow,
      paymentMethod: item.paymentMethod || '',
      frontendHint: item.frontendHint || '',
      metadataJson: item.metadataJson || ''
    });

    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.editingId = null;
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.notifyService.showError('Please complete the form correctly.', 'Validation');
      return;
    }

    this.saving = true;

    const payload: CreateCurrencyPaymentGatewayRequestDto | UpdateCurrencyPaymentGatewayRequestDto = {
      currencyCode: String(this.form.value.currencyCode || '').trim().toUpperCase(),
      gatewayConfigId: this.form.value.gatewayConfigId,
      isDefault: !!this.form.value.isDefault,
      isActive: !!this.form.value.isActive,
      priority: Number(this.form.value.priority || 1),
      minAmount: this.toNullableNumber(this.form.value.minAmount),
      maxAmount: this.toNullableNumber(this.form.value.maxAmount),
      customerType: this.toNullableString(this.form.value.customerType),
      countryCode: this.toNullableUpper(this.form.value.countryCode),
      workflow: this.form.value.workflow,
      paymentMethod: this.toNullableString(this.form.value.paymentMethod),
      frontendHint: this.toNullableString(this.form.value.frontendHint),
      metadataJson: this.toNullableString(this.form.value.metadataJson)
    };

    const request$ = this.editingId
      ? this.withdrawalAdminService.updateCurrencyMapping(this.editingId, payload)
      : this.withdrawalAdminService.createCurrencyMapping(payload);

    request$.subscribe({
      next: () => {
        this.notifyService.showSuccess(
          this.editingId ? 'Gateway mapping updated successfully' : 'Gateway mapping created successfully',
          'Success'
        );
        this.closeModal();
        this.loadMappings();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to save gateway mapping',
          'Error'
        );
      },
      complete: () => {
        this.saving = false;
      }
    });
  }

  setDefault(item: CurrencyPaymentGatewayDto): void {
    this.withdrawalAdminService.setCurrencyMappingDefault(item.id).subscribe({
      next: () => {
        this.notifyService.showSuccess('Default gateway mapping updated', 'Success');
        this.loadMappings();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to set default gateway mapping',
          'Error'
        );
      }
    });
  }

  toggleStatus(item: CurrencyPaymentGatewayDto): void {
    this.withdrawalAdminService.toggleCurrencyMappingStatus(item.id).subscribe({
      next: () => {
        this.notifyService.showSuccess('Gateway mapping status updated', 'Success');
        this.loadMappings();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to toggle gateway mapping status',
          'Error'
        );
      }
    });
  }

  badgeClass(active: boolean): string {
    return active ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger';
  }

  boolBadgeClass(flag: boolean): string {
    return flag ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary';
  }

  private toNullableString(value: any): string | null {
    const v = String(value ?? '').trim();
    return v ? v : null;
  }

  private toNullableUpper(value: any): string | null {
    const v = String(value ?? '').trim().toUpperCase();
    return v ? v : null;
  }

  private toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}
